import React from "react";
import { motion } from "framer-motion";
import {
  Sparkles, Play, TrendingUp, Bot, FileText, Target, ArrowUpRight,
  CheckCircle2, Activity, MessageSquare, LayoutDashboard, FolderKanban,
  Compass, Megaphone, PiggyBank, BarChart3, Settings, Bell, Search, Loader2
} from "lucide-react";
import { FloatingBlobs, fadeUp } from "./Primitives";
import TrustedBy from "./TrustedBy";

const Bar = ({ label, value, color }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-gray-600 w-24 truncate">{label}</span>
    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${value}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1.4, ease: "easeOut" }}
        className="h-full rounded-full"
        style={{ background: color }}
      />
    </div>
    <span className="text-xs font-semibold text-gray-800 w-9 text-right">{value}%</span>
  </div>
);

export default function Hero({ onGetStarted, onWatchDemo }) {
  return (
    <section id="home" className="relative pt-32 md:pt-40 pb-20 lg:pb-28 overflow-hidden">
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <FloatingBlobs />

      <div className="relative z-20 max-w-[1600px] w-full mx-auto px-4 sm:px-6 md:px-10 lg:px-12 grid lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-12 items-center">
        {/* LEFT */}
        <motion.div
          initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.1 } } }}
          className="lg:col-span-6 pointer-events-auto"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass border border-[#7C3AED]/15 text-xs font-bold tracking-[0.18em] uppercase text-[#7C3AED] shadow-xs">
            <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899]" />
            AI Powered Business Intelligence
          </motion.div>

          <motion.h1 variants={fadeUp} className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tighter text-gray-900">
            <span className="block">Build.</span>
            <span className="block">Launch.</span>
            <span className="block brand-gradient-text">Scale your startup</span>
            <span className="block">with AI.</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-6 max-w-3xl lg:max-w-4xl text-lg text-gray-600 leading-relaxed">
            STUDLYF AI helps founders, students, agencies, incubators and investors transform raw ideas into
            successful businesses — with AI-powered strategy, market intelligence, branding, marketing and
            investor tools that actually work.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={onGetStarted}
              className="glow-button rounded-full px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 shadow-lg transition-all duration-300"
              data-testid="hero-start-free"
            >
              <Sparkles className="w-4 h-4" /> Start Free
            </button>
            <button
              onClick={onWatchDemo}
              className="rounded-full px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 glass hover:bg-white/90 transition-all duration-300"
              data-testid="hero-watch-demo"
            >
              <Play className="w-4 h-4" /> Watch Demo
            </button>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-10 flex items-center gap-5 text-xs text-gray-500">
            <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#22C55E]" /> No credit card</div>
            <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#22C55E]" /> 14-day Pro trial</div>
            <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#22C55E]" /> Cancel anytime</div>
          </motion.div>
        </motion.div>

        {/* RIGHT — Premium Handcrafted SaaS Workspace Mock */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative w-full pointer-events-auto"
        >
          {/* background glow */}
          <div className="absolute -inset-6 rounded-[40px] bg-gradient-to-br from-[#7C3AED]/25 via-[#EC4899]/15 to-[#A855F7]/15 blur-3xl pointer-events-none" />

          {/* main browser frame mockup */}
          <div className="relative glass rounded-[24px] border border-white/70 overflow-hidden shadow-[0_24px_50px_rgba(0,0,0,0.06)] bg-white/80 backdrop-blur-md text-gray-800 text-left font-sans flex flex-col h-[520px] w-full">
            
            {/* TOP HEADER */}
            <div className="h-14 border-b border-gray-100/70 bg-white/90 flex items-center justify-between px-4 shrink-0">
              <div className="flex items-center gap-2">
                {/* Window Dots */}
                <div className="flex gap-1.5 shrink-0">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#EC4899]/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]/70" />
                </div>
                <div className="ml-3 text-[11px] font-medium text-gray-400 bg-gray-50/50 border border-gray-100 rounded-md px-2 py-0.5 whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px] md:max-w-none">
                  studlyf.ai/workspace
                </div>
              </div>

              {/* Header Right */}
              <div className="flex items-center gap-2.5 text-xs font-semibold">
                {/* Search box placeholder */}
                <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-100 text-gray-400 font-normal">
                  <Search className="w-3.5 h-3.5" />
                  <span className="text-[10px]">Search...</span>
                </div>
                {/* Project Picker dropdown look */}
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#F4F1FF] text-[#7C3AED] border border-[#7C3AED]/10 text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />
                  <span>EcoVolt AI</span>
                </div>
                {/* Notification */}
                <div className="relative p-1 rounded-md hover:bg-gray-100 text-gray-400 cursor-pointer">
                  <Bell className="w-3.5 h-3.5" />
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#EC4899] animate-pulse" />
                </div>
                {/* Avatar */}
                <div className="w-6.5 h-6.5 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center font-bold text-[10px]">
                  E
                </div>
              </div>
            </div>

            {/* BODY LAYOUT */}
            <div className="flex-1 flex overflow-hidden min-h-0 bg-white/20">
              
              {/* LEFT SIDEBAR */}
              <aside className="w-40 border-r border-gray-100/70 bg-white/50 py-3 px-2 flex flex-col gap-0.5 shrink-0 select-none hidden sm:flex">
                {[
                  { label: "AI Workspace", icon: MessageSquare, active: true },
                  { label: "BI Dashboard", icon: LayoutDashboard },
                  { label: "Projects", icon: FolderKanban },
                  { label: "Strategy", icon: Compass },
                  { label: "Marketing", icon: Megaphone },
                  { label: "Funding", icon: PiggyBank },
                  { label: "Documents", icon: FileText },
                  { label: "Analytics", icon: BarChart3 },
                  { label: "Settings", icon: Settings },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[10.5px] font-medium transition-colors ${
                      item.active
                        ? "bg-[#F4F1FF] text-[#7C3AED] font-semibold border border-[#7C3AED]/5"
                        : "text-gray-500 hover:bg-gray-50/50 hover:text-gray-900"
                    }`}
                  >
                    <item.icon className={`w-3.5 h-3.5 ${item.active ? "text-[#7C3AED]" : "text-gray-400"}`} />
                    <span className="truncate">{item.label}</span>
                  </div>
                ))}
              </aside>

              {/* MAIN CONTENT AREA */}
              <div className="flex-1 flex flex-col min-w-0">
                {/* Upper row: Content grid */}
                <div className="flex-1 flex overflow-hidden min-h-0">
                  
                  {/* MAIN CONTENT COLUMN */}
                  <div className="flex-1 p-4 flex flex-col gap-4 overflow-y-auto no-scrollbar border-r border-gray-100/60">
                    {/* Startup Overview */}
                    <div>
                      <div className="flex items-center justify-between">
                        <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Startup Overview</h4>
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Stage: Seed</span>
                      </div>
                      <div className="mt-2 bg-white border border-gray-100 rounded-xl p-3 shadow-xs">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-gray-800">EcoVolt AI</span>
                          <span className="font-bold text-[#7C3AED]">82% Complete</span>
                        </div>
                        <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden mt-1.5">
                          <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] rounded-full" style={{ width: "82%" }} />
                        </div>

                        {/* Believable Stats */}
                        <div className="grid grid-cols-2 gap-2 mt-3.5">
                          <div className="bg-gray-50/50 p-2 rounded-lg border border-gray-100/60">
                            <p className="text-[8.5px] font-bold text-gray-400 uppercase tracking-wider">Monthly Users</p>
                            <p className="text-xs font-bold text-gray-800 mt-0.5">14,280</p>
                          </div>
                          <div className="bg-gray-50/50 p-2 rounded-lg border border-gray-100/60">
                            <p className="text-[8.5px] font-bold text-gray-400 uppercase tracking-wider">MRR Revenue</p>
                            <p className="text-xs font-bold text-gray-800 mt-0.5">₹2,84,500</p>
                          </div>
                          <div className="bg-gray-50/50 p-2 rounded-lg border border-gray-100/60">
                            <p className="text-[8.5px] font-bold text-gray-400 uppercase tracking-wider">Active Agents</p>
                            <p className="text-xs font-bold text-gray-800 mt-0.5">8 Agents</p>
                          </div>
                          <div className="bg-gray-50/50 p-2 rounded-lg border border-gray-100/60">
                            <p className="text-[8.5px] font-bold text-gray-400 uppercase tracking-wider">Documents</p>
                            <p className="text-xs font-bold text-gray-800 mt-0.5">18 Files</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Recent Tasks */}
                    <div>
                      <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Recent Tasks</h4>
                      <div className="space-y-1.5 text-[10px]">
                        {[
                          { t: "Generate Business Plan", s: "Complete", c: "bg-green-50 text-green-600 border-green-100" },
                          { t: "Market Research", s: "Running...", c: "bg-blue-50 text-blue-600 border-blue-100", active: true },
                          { t: "Financial Forecast", s: "Complete", c: "bg-green-50 text-green-600 border-green-100" },
                          { t: "Pitch Deck Mock", s: "Review Required", c: "bg-amber-50 text-amber-600 border-amber-100" },
                          { t: "Brand Identity Layout", s: "Complete", c: "bg-green-50 text-green-600 border-green-100" },
                        ].map((task, idx) => (
                          <div key={idx} className="flex items-center justify-between p-1.5 bg-white border border-gray-100/60 rounded-lg shadow-2xs">
                            <span className="font-semibold text-gray-700">{task.t}</span>
                            <span className={`text-[8.5px] font-bold px-1.5 py-0.5 rounded-md border ${task.c} flex items-center gap-1`}>
                              {task.active && <span className="w-1 h-1 rounded-full bg-blue-500 animate-ping" />}
                              {task.s}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* RIGHT COLUMN (AI ACTIVITY) */}
                  <div className="w-[180px] p-4 bg-gray-50/40 flex flex-col gap-3 overflow-y-auto no-scrollbar hidden md:flex shrink-0">
                    <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">AI Activity</h4>
                    <div className="space-y-3.5 mt-1">
                      {[
                        { agent: "Strategy Agent", act: "completed SWOT", color: "bg-[#7C3AED]" },
                        { agent: "Marketing Agent", act: "generated GTM Plan", color: "bg-[#EC4899]" },
                        { agent: "Investor Agent", act: "updated Financial Model", color: "bg-green-500" },
                        { agent: "Analytics Agent", act: "created KPI Dashboard", color: "bg-blue-500" },
                      ].map((item, idx) => (
                        <div key={idx} className="flex gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${item.color} mt-1.5 shrink-0`} />
                          <div className="text-[9.5px]">
                            <p className="font-bold text-gray-800 leading-tight">{item.agent}</p>
                            <p className="text-gray-500 leading-normal mt-0.5">{item.act}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>

                {/* BOTTOM PANEL (QUICK ACTIONS) */}
                <div className="border-t border-gray-100/70 bg-white/60 p-3 shrink-0">
                  <h4 className="text-[9.5px] font-bold text-gray-400 uppercase tracking-wider mb-2">Quick Actions</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      "Generate Pitch Deck", "Run Market Research", "Create Investor One Pager",
                      "Build Business Model Canvas", "Brand Kit Generator", "Generate Financial Forecast"
                    ].map((act, idx) => (
                      <button
                        key={idx}
                        className="px-2 py-1 rounded-md border border-gray-100 bg-white text-[9.5px] font-semibold text-gray-600 hover:border-[#7C3AED]/30 hover:bg-[#F4F1FF] hover:text-[#7C3AED] hover:scale-[1.02] transition-all duration-200 shadow-3xs"
                      >
                        {act}
                      </button>
                    ))}
                  </div>
                </div>

              </div>

            </div>

          </div>
        </motion.div>
      </div>
    </section>
  );
}
